import * as React from 'react';
import axios from 'axios';
import {
  Alert,
  Heading,
  Select,
  TextField,
  Button,
  Row,
  Col,
} from '@elevationscu/ui-component-lib';

import styles from '../App.module.scss';
import ProgressBar from './ProgressBar';
import InputRequest from './input-request';
import DataDisclaimer from './data-disclaimer';
import prospectPut from './helper-functions';
import { EMAIL_REGEX, PHONE_REGEX } from './constants';

function formatPhoneNumber(value) {
  if (!value) return value;
  // replaces all non integer characters
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  // formats number to be (xxx) xxx
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  // formats number to be (xxx) xxx-xxxx
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
}

export default function PersonalInfo({
  pageNumber, userInfo, setUserInfo, nextPage, maxPages,
}) {
  const [first_name, setFirst_name] = React.useState(null);
  const [last_name, setLast_name] = React.useState(null);
  const [email, setEmail] = React.useState(null);
  const [phone, setPhone] = React.useState(null);
  const [second_contact_first_name, setSecondContactFirstName] = React.useState(null);
  const [second_contact_last_name, setSecondContactLastName] = React.useState(null);
  const [second_contact_email, setSecondContactEmail] = React.useState(null);
  const [second_contact_phone, setSecondContactPhone] = React.useState(null);
  const [depositedPhone, setDepositedPhone] = React.useState(phone);
  const [depositedSecondContactPhone, setSecondContactDepositedPhone] = React.useState(second_contact_phone);
  const [loanOfficers, setLoanOfficers] = React.useState([]);
  const [realtor, setRealtor] = React.useState();
  const [loan_officer_id, setLoan_officer_id] = React.useState(null);
  const [isFirstNameError, setFirstNameError] = React.useState(null);
  const [isLastNameError, setLastNameError] = React.useState(null);
  const [isEmailError, setEmailError] = React.useState(null);
  const [isSecondContactEmailError, setSecondContactEmailError] = React.useState(null);
  const [isPhoneError, setPhoneError] = React.useState(null);
  const [isSecondContactPhoneError, setSecondContactPhoneError] = React.useState(null);
  const [isButtonDisabled, setButtonDisabled] = React.useState(true);
  const [apiError, setApiError] = React.useState(null);
  const [isPassedLO, setIsPassedLO] = React.useState(false);
  const [passedLoName, setPassedLoName] = React.useState('');
  const [passedLoID, setPassedLoID] = React.useState(null);
  const passedLO = decodeURIComponent(window.location.search);
  const sanitizedLO = passedLO.replace('?', '');
  const validationSchema = {
    firstName: {
      required: 'First name is required',
      maxLength: 50,
    },
    lastName: {
      required: 'Last name is required',
      maxLength: 50,
    },
    email: {
      required: 'Valid email is required',
      maxLength: 100,
      validate: (value) => EMAIL_REGEX.test(value) || 'A valid email address is required',
    },
    phoneNumber: {
      message: 'Phone number must be 10 digits',
      validate: (value) => PHONE_REGEX.test(value) || 'Please enter your full phone number, including area code in the following format XXX-XXX-XXXX',
    },
  };

  React.useEffect(() => {
    if ((first_name !== '' && first_name !== null) && (last_name !== '' && last_name !== null) && (EMAIL_REGEX.test(email)) && (phone === null || phone === '' || phone.length === 14) && !isSecondContactEmailError && !isSecondContactPhoneError) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [first_name, last_name, email, phone, isSecondContactEmailError, isSecondContactPhoneError]);

  React.useEffect(() => {
    if (userInfo.first_name) setFirst_name(userInfo.first_name);
    if (userInfo.last_name) setLast_name(userInfo.last_name);
    if (userInfo.email) setEmail(userInfo.email);
    if (userInfo.phone) setPhone(formatPhoneNumber(userInfo.phone));
    if (userInfo.phone) setDepositedPhone(userInfo.phone);
    if (userInfo.second_contact_first_name) setSecondContactFirstName(userInfo.second_contact_first_name);
    if (userInfo.second_contact_last_name) setSecondContactLastName(userInfo.second_contact_last_name);
    if (userInfo.second_contact_email) setSecondContactEmail(userInfo.second_contact_email);
    if (userInfo.second_contact_phone) {
      setSecondContactPhone(formatPhoneNumber(userInfo.second_contact_phone));
      setSecondContactDepositedPhone(userInfo.second_contact_phone);
    }
    if (userInfo.loan_officer_id) setLoan_officer_id(userInfo.loan_officer_id);
    if (userInfo.application_data.realtor) setRealtor(userInfo.application_data.realtor);
    // eslint-disable-next-line
    }, [])

  React.useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/loan-officers`,
        );
        setLoanOfficers(response.data.officers);
        response.data.officers.forEach((officer) => {
          const tmpLO = `${officer.first_name}-${officer.last_name}`;
          const LoName = `${officer.first_name} ${officer.last_name}`;
          if (sanitizedLO === tmpLO) {
            setIsPassedLO(true);
            setPassedLoName(LoName);
            setPassedLoID(officer.id);
            setLoan_officer_id((officer.id).toString());
          }
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [sanitizedLO]);

  async function next() {
    if (loan_officer_id === '') {
      setLoan_officer_id(null);
    }
    userInfo.application_data = Object.assign(userInfo.application_data, {
      realtor,
    });
    setUserInfo(
      Object.assign(userInfo, {
        first_name,
        last_name,
        email,
        phone: depositedPhone,
        second_contact_first_name,
        second_contact_last_name,
        second_contact_email,
        second_contact_phone: depositedSecondContactPhone,
        loan_officer_id,
        app_status: 'Incomplete',
        // application_data: { realtor },
      }),
    );
    const response = await prospectPut(userInfo);
    if (response?.status === 200 || response?.status === 201) {
      const newUserInfo = { ...userInfo, application_id: response?.data?.application_id };
      nextPage(newUserInfo);
    } else if (response?.status === 400) {
      setApiError("We're sorry for the inconvenience, but we're unable to process your request. Please call 800.460.1620 for immediate assistance.");
    }
  }

  function handleInput(e) {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    const integerOnlyFormat = formattedPhoneNumber.replace(/\D/g, '');
    // phone number displayed to the user, formatted as (xxx) xxx-xxxx
    e.target.value = formattedPhoneNumber;
    setPhone(formattedPhoneNumber);
    // number deposited to the DB, stripped of all non-integer characters
    if (integerOnlyFormat === '') {
      setDepositedPhone(null);
    } else {
      setDepositedPhone(integerOnlyFormat);
    }
  }

  function handleSecondContactPhoneInput(e) {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);
    const integerOnlyFormat = formattedPhoneNumber.replace(/\D/g, '');
    // phone number displayed to the user, formatted as (xxx) xxx-xxxx
    e.target.value = formattedPhoneNumber;
    setSecondContactPhone(formattedPhoneNumber);
    // number deposited to the DB, stripped of all non-integer characters
    if (integerOnlyFormat === '') {
      setSecondContactDepositedPhone(null);
    } else {
      setSecondContactDepositedPhone(integerOnlyFormat);
    }
  }

  return (
    <div className={styles.coreData}>
      <div className={styles.coreHeader}>
        <Heading size={2} element="h1" className={styles.dataFormHeader}>
          First, let's learn a little about you
        </Heading>
        <InputRequest firstPage />
      </div>
      <div className={styles.prospectInput}>
        <ProgressBar
          pageNumber={pageNumber}
          maxPages={maxPages}
        />
        <Heading size={3} element="h2" className={styles.inputHeader}>
          Your information
        </Heading>
        <div className={styles.dataFormSpan}>
          Please provide your contact information so we can contact you to discuss your rate proposal.
        </div>
        <div className={styles.dataInputs}>
          <Row>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="First name (required)"
                  id="inputFirstName"
                  value={first_name}
                  onChange={(e) => {
                    setFirst_name(e.target.value);
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || e.target.value === null) {
                      setFirstNameError(true);
                    } else {
                      setFirstNameError(false);
                    }
                  }}
                  required
                  error={!isFirstNameError ? isFirstNameError : validationSchema.firstName.required}
                />
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>

                <TextField
                  type="text"
                  label="Last name (required)"
                  id="inputLastName"
                  value={last_name}
                  onChange={(e) => setLast_name(e.target.value)}
                  error={!isLastNameError ? isLastNameError : validationSchema.lastName.required}
                  onBlur={(e) => {
                    if (e.target.value === '' || e.target.value === null) {
                      setLastNameError(true);
                    } else {
                      setLastNameError(false);
                    }
                  }}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Email (required)"
                  id="inputUserEmail"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  error={!isEmailError ? isEmailError : validationSchema.email.required}
                  onBlur={(e) => {
                    if (!EMAIL_REGEX.test(e.target.value)) {
                      setEmailError(true);
                    } else {
                      setEmailError(false);
                    }
                  }}
                />
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  label="Phone number (optional)"
                  data-testid="testPhoneNumber"
                  id="inputPhoneNumber"
                  onChange={(e) => handleInput(e)}
                  value={phone}
                  error={!isPhoneError ? isPhoneError : validationSchema.phoneNumber.message}
                  onBlur={(e) => {
                    if (e.target.value !== null && e.target.value.length !== 14 && e.target.value !== '') {
                      setPhoneError(true);
                    } else {
                      setPhoneError(false);
                    }
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
        <Heading size={3} element="h2" className={styles.secondInputHeader}>
          Secondary Contact Information (Optional)
        </Heading>
        <div className={styles.dataInputs}>
          <Row>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="First name (optional)"
                  id="inputSecondContactFirstName"
                  value={second_contact_first_name}
                  onChange={(e) => {
                    setSecondContactFirstName(e.target.value.trim());
                  }}
                  onBlur={(e) => {
                    setSecondContactFirstName(e.target.value.trim());
                  }}
                />
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Last name (optional)"
                  id="inputSecondContactLastName"
                  value={second_contact_last_name}
                  onChange={(e) => {
                    setSecondContactLastName(e.target.value.trim());
                  }}
                  onBlur={(e) => {
                    setSecondContactLastName(e.target.value.trim());
                  }}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  type="text"
                  label="Email (optional)"
                  id="inputSecondContactUserEmail"
                  value={second_contact_email}
                  onChange={(e) => {
                    setSecondContactEmail(e.target.value.trim());
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim().length && !EMAIL_REGEX.test(e.target.value.trim())) {
                      setSecondContactEmailError(true);
                    } else {
                      setSecondContactEmailError(false);
                    }
                  }}
                  error={isSecondContactEmailError ? 'Invalid email address' : ''}
                />
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className={styles.indivInput}>
                <TextField
                  label="Phone number (optional)"
                  data-testid="testSecondContactPhoneNumber"
                  id="inputSecondContactPhoneNumber"
                  onChange={(e) => handleSecondContactPhoneInput(e)}
                  value={second_contact_phone}
                  onBlur={(e) => {
                    if (e.target.value !== null && e.target.value.length !== 14 && e.target.value !== '') {
                      setSecondContactPhoneError(true);
                    } else {
                      setSecondContactPhoneError(false);
                    }
                  }}
                  error={isSecondContactPhoneError ? validationSchema.phoneNumber.message : ''}
                />
              </div>
            </Col>
          </Row>
        </div>
        <Row>
          <Col>
            <div className={styles.loanOfficerOptions}>
              <div>
                <Heading
                  size={3}
                  element="h2"
                  style={{ margin: '20px 0px 20px 0px' }}
                >
                  Do you have a loan officer you'd like to work with?
                </Heading>
              </div>
            </div>
            <Select
              name="loanOfficers"
              id="selectLoanOfficer"
              value={loan_officer_id}
              label="Select a loan officer (optional)"
              style={{ paddingLeft: '5px' }}
              onChange={(e) => {
                if (e.target.value === '') {
                  setLoan_officer_id(null);
                } else {
                  setLoan_officer_id(e.target.value);
                }
              }}
            >
              {!isPassedLO
                                    && (
                                    <option value="" selected>
                                      No Preference
                                    </option>
                                    )}
              {isPassedLO
                                    && (
                                    <option value={passedLoID} selected>
                                      {passedLoName}
                                    </option>
                                    )}
              {loanOfficers.length > 0
                                    && loanOfficers.map((loanOfficer) => (
                                      <option value={loanOfficer.id}>
                                        {' '}
                                        {loanOfficer.first_name}
                                        {' '}
                                        {loanOfficer.last_name}
                                      </option>
                                    ))}
            </Select>
          </Col>
          <Col>
            <div className={styles.loanOfficerOptions}>
              <div>
                <Heading
                  size={3}
                  element="h2"
                  style={{ margin: '20px 0px 20px 0px' }}
                >
                  Did a real estate agent send you to Elevations?
                </Heading>
              </div>
            </div>
            <TextField
              type="text"
              label="Enter agent's name (optional)"
              id="inputRealEstateAgentName"
              value={realtor}
              onChange={(e) => { setRealtor(e.target.value); }}
            />
          </Col>

        </Row>
        {apiError
                            && (
                            <Row>
                              <Col>
                                <div>
                                  <Alert variant="error">
                                    {apiError}
                                  </Alert>
                                </div>
                              </Col>
                            </Row>
                            )}
        <Row>
          <Col lg={8}>
            <DataDisclaimer firstPage />
          </Col>
          <Col className={styles.nextButtonCol}>
            <div className={styles.nextButton}>
              <Button onClick={next} disabled={isButtonDisabled} id="dataNextButton">
                Next
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      {/* Removing for Release 1, will reinstate later */}

      {/* <div className={styles.previousProposal}>
                Already have a rate proposal?
                <span
                    className={styles.clickablePreviousProposal}
                    id="previousProposal"
                >
                    {" "}
                    Click here to view{" "}
                </span>
            </div> */}
    </div>
  );
}
